import { loadStripe } from '@stripe/stripe-js';
import { createSetupCheckout, createPortalSession } from './subscriptionService';

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';

if (!stripePublishableKey) {
  console.error('VITE_STRIPE_PUBLISHABLE_KEY is not configured');
}

const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

export interface SetupCheckoutOptions {
  intendedTier: string;
  successUrl?: string;
  cancelUrl?: string;
}

export async function redirectToSetupCheckout(
  options: SetupCheckoutOptions
): Promise<void> {
  try {
    const { intendedTier, successUrl, cancelUrl } = options;

    // Include tier in success URL so Dashboard can redirect to subscription checkout
    const defaultSuccessUrl = `${window.location.origin}/dashboard?setup=success&tier=${encodeURIComponent(intendedTier)}`;
    const defaultCancelUrl = `${window.location.origin}/dashboard?setup=cancel`;

    const { url } = await createSetupCheckout(
      intendedTier,
      successUrl || defaultSuccessUrl,
      cancelUrl || defaultCancelUrl
    );

    if (!url) {
      throw new Error('Failed to create checkout session. Please try again.');
    }

    // Redirect directly to Stripe checkout URL
    window.location.href = url;
  } catch (error: any) {
    console.error('Error in redirectToSetupCheckout:', error);
    throw new Error(error?.message || 'Failed to start checkout process');
  }
}

export interface SubscriptionCheckoutOptions {
  tier: string;
  successUrl?: string;
  cancelUrl?: string;
}

export async function redirectToSubscriptionCheckout(
  options: SubscriptionCheckoutOptions
): Promise<void> {
  try {
    const { tier, successUrl, cancelUrl } = options;

    const defaultSuccessUrl = `${window.location.origin}/dashboard?subscription=success`;
    const defaultCancelUrl = `${window.location.origin}/dashboard?subscription=cancel`;

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-create-subscription-checkout`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          tier,
          successUrl: successUrl || defaultSuccessUrl,
          cancelUrl: cancelUrl || defaultCancelUrl,
        }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to create subscription checkout session');
    }

    const { url } = await response.json();

    if (!url) {
      throw new Error('Failed to create checkout session. Please try again.');
    }

    // Redirect directly to Stripe checkout URL
    window.location.href = url;
  } catch (error) {
    console.error('Error in redirectToSubscriptionCheckout:', error);
    throw error;
  }
}

export async function redirectToBillingPortal(returnUrl?: string): Promise<void> {
  try {
    const defaultReturnUrl = `${window.location.origin}/dashboard`;
    const { url } = await createPortalSession(returnUrl || defaultReturnUrl);

    window.location.href = url;
  } catch (error) {
    console.error('Error redirecting to billing portal:', error);
    throw error;
  }
}

export interface UpgradeDowngradeOptions {
  newTier: string;
  successUrl?: string;
  cancelUrl?: string;
}

export async function upgradeOrDowngradeSubscription(
  options: UpgradeDowngradeOptions
): Promise<void> {
  try {
    const { newTier, successUrl, cancelUrl } = options;

    const defaultSuccessUrl = `${window.location.origin}/dashboard?change=success`;
    const defaultCancelUrl = `${window.location.origin}/dashboard?change=cancel`;

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-change-subscription`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          newTier,
          successUrl: successUrl || defaultSuccessUrl,
          cancelUrl: cancelUrl || defaultCancelUrl,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to change subscription');
    }

    const result = await response.json();

    // Subscription change is immediate, no checkout needed
    // Redirect to success URL
    window.location.href = defaultSuccessUrl;
  } catch (error) {
    console.error('Error in upgradeOrDowngradeSubscription:', error);
    throw error;
  }
}

export interface ReportCheckoutOptions {
  successUrl?: string;
  cancelUrl?: string;
}

export async function createReportCheckout(
  options: ReportCheckoutOptions = {}
): Promise<{ url: string }> {
  try {
    const { successUrl, cancelUrl } = options;

    const defaultSuccessUrl = `${window.location.origin}/thank-you?session_id={CHECKOUT_SESSION_ID}`;
    const defaultCancelUrl = `${window.location.origin}/pricing`;

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-create-report-checkout`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          successUrl: successUrl || defaultSuccessUrl,
          cancelUrl: cancelUrl || defaultCancelUrl,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create report checkout session');
    }

    const { url } = await response.json();

    if (!url) {
      throw new Error('No checkout URL received');
    }

    return { url };
  } catch (error) {
    console.error('Error creating report checkout:', error);
    throw error;
  }
}

export interface HybridCheckoutOptions {
  tier: 'starter' | 'pro' | 'premium';
  successUrl?: string;
  cancelUrl?: string;
}

export async function createHybridCheckout(
  options: HybridCheckoutOptions
): Promise<{ url: string }> {
  try {
    const { tier, successUrl, cancelUrl } = options;

    const defaultSuccessUrl = `${window.location.origin}/thank-you?session_id={CHECKOUT_SESSION_ID}`;
    const defaultCancelUrl = `${window.location.origin}/pricing`;

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-create-hybrid-checkout`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          tier,
          successUrl: successUrl || defaultSuccessUrl,
          cancelUrl: cancelUrl || defaultCancelUrl,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create hybrid checkout session');
    }

    const { url } = await response.json();

    if (!url) {
      throw new Error('No checkout URL received');
    }

    return { url };
  } catch (error) {
    console.error('Error creating hybrid checkout:', error);
    throw error;
  }
}

export interface UpsellCheckoutOptions {
  tier: 'starter' | 'pro' | 'premium';
  customerId?: string;
  successUrl?: string;
  cancelUrl?: string;
}

export async function createUpsellCheckout(
  options: UpsellCheckoutOptions
): Promise<{ url: string }> {
  try {
    const { tier, customerId, successUrl, cancelUrl } = options;

    const defaultSuccessUrl = `${window.location.origin}/dashboard?subscription=success`;
    const defaultCancelUrl = `${window.location.origin}/thank-you`;

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-create-upsell-checkout`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          tier,
          customerId,
          successUrl: successUrl || defaultSuccessUrl,
          cancelUrl: cancelUrl || defaultCancelUrl,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create upsell checkout session');
    }

    const { url } = await response.json();

    if (!url) {
      throw new Error('No checkout URL received');
    }

    return { url };
  } catch (error) {
    console.error('Error creating upsell checkout:', error);
    throw error;
  }
}
